import React, { useEffect, useMemo, useState } from 'react'
import { Eye, EyeOff, Plus, RotateCcw, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { authorizedGraphQLRequest } from '@/api/graphqlClient'
import { Button } from '@/components/ui/button'
import { PAYWALL_ICONS, PaywallPage, REASSURANCE_ICONS, type CardLanguage, type LocalizedText, type PaywallContent, type PaywallPlan, type ReassuranceItem } from '@/components/PlanCard'
import LocalizedInput, { ErrorFieldContext } from '@/pages/PlanCards/LocalizedInput'
import { LanguageSwitch } from '@/pages/PlanCards/TemplateEditor'
import { GET_PAYWALL_CONTENT, SET_PAYWALL_CONTENT, blankText, coverage, paywallTexts, serverError } from '@/pages/PlanCards/editing'

/**
 * A B2C organisation's paywall texts: title, intro, reassurance items and footnote. A
 * field left empty shows the built-in text. Messages about payments and ended
 * subscriptions are not editable; the paywall always shows them.
 */

const MAX_REASSURANCE = 4
const EMPTY: PaywallContent = { version: 1, reassurance: [] }

const ICON_NAMES: Record<ReassuranceItem['icon'], string> = { refresh: 'Renew', shield: 'Shield', clock: 'Clock', headset: 'Support', chart: 'Progress', message: 'Message' }

/** Blank fields are dropped; nothing left means the built-in texts. */
const cleanContent = (content: PaywallContent): PaywallContent | null => {
  const out: PaywallContent = { version: 1, reassurance: content.reassurance }
  for (const field of ['title', 'intro', 'footnote'] as const) {
    if (!blankText(content[field])) out[field] = content[field]
  }
  const empty = !out.title && !out.intro && !out.footnote && out.reassurance.length === 0
  return empty ? null : out
}

interface PaywallContentPanelProps {
  organisationId: number
  /** Active plans with their cards, for the page preview. */
  plans: PaywallPlan[]
}

const PaywallContentPanel: React.FC<PaywallContentPanelProps> = ({ organisationId, plans }) => {
  const { i18n } = useTranslation()
  const [saved, setSaved] = useState<PaywallContent | null>(null)
  const [content, setContent] = useState<PaywallContent>(EMPTY)
  const [language, setLanguage] = useState<CardLanguage>('en')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [showPreview, setShowPreview] = useState(false)
  const [error, setError] = useState<{ message: string; field: string | null } | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    authorizedGraphQLRequest<{ getPaywallContent: PaywallContent | null }>(GET_PAYWALL_CONTENT, { organisationId })
      .then((response) => {
        if (cancelled) return
        setSaved(response.getPaywallContent)
        setContent(response.getPaywallContent ?? EMPTY)
      })
      .catch((err) => !cancelled && setError(serverError(err, 'Failed to load the paywall texts')))
      .finally(() => !cancelled && setLoading(false))
    return () => {
      cancelled = true
    }
  }, [organisationId])

  const cleaned = useMemo(() => cleanContent(content), [content])
  const dirty = JSON.stringify(cleaned) !== JSON.stringify(saved)
  const t = i18n.getFixedT(language)

  const save = async () => {
    setError(null)
    setSaving(true)
    try {
      const response = await authorizedGraphQLRequest<{ setPaywallContent: PaywallContent | null }>(SET_PAYWALL_CONTENT, { organisationId, content: cleaned })
      setSaved(response.setPaywallContent)
      setContent(response.setPaywallContent ?? EMPTY)
    } catch (err) {
      setError(serverError(err, 'Failed to save the paywall texts'))
    } finally {
      setSaving(false)
    }
  }

  const setItem = (index: number, item: ReassuranceItem) => setContent((c) => ({ ...c, reassurance: c.reassurance.map((it, i) => (i === index ? item : it)) }))

  const field = (name: 'title' | 'intro' | 'footnote', label: string, builtIn: string, multiline = false) => (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <label className="text-[11px] font-medium text-gray-600">{label}</label>
        {!blankText(content[name]) && (
          <button type="button" onClick={() => setContent((c) => ({ ...c, [name]: undefined }))} className="inline-flex items-center gap-1 text-[11px] text-gray-500 hover:text-gray-800">
            <RotateCcw className="h-3 w-3" /> Default
          </button>
        )}
      </div>
      <LocalizedInput
        language={language}
        path={name}
        value={content[name]}
        inherited={builtIn}
        multiline={multiline}
        optional
        tokens={false}
        onChange={(value: LocalizedText) => setContent((c) => ({ ...c, [name]: value }))}
      />
    </div>
  )

  if (loading) return <div className="text-sm text-gray-500">Loading paywall texts…</div>

  const [filled, total] = coverage(paywallTexts(content))[language] ?? [0, 0]

  return (
    <ErrorFieldContext.Provider value={error?.field ?? null}>
      <div className="space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h5 className="font-semibold text-gray-800">Paywall page</h5>
            <p className="text-xs text-gray-500">What users without a subscription read around the plans. Empty fields use the built-in text, shown in grey.</p>
          </div>
          <div className="flex items-center gap-2">
            <LanguageSwitch value={language} onChange={setLanguage} coverage={coverage(paywallTexts(content))} />
            <Button size="sm" variant="outline" onClick={() => setShowPreview((v) => !v)}>
              {showPreview ? <EyeOff className="mr-1 h-3.5 w-3.5" /> : <Eye className="mr-1 h-3.5 w-3.5" />}
              {showPreview ? 'Hide preview' : 'Preview page'}
            </Button>
          </div>
        </div>
        {total > 0 && filled < total && language !== 'en' && <p className="text-xs text-amber-700">{total - filled} text(s) not translated: users reading this language see the English.</p>}

        {field('title', 'Title', t('subscription.paywall.title'))}
        {field('intro', 'Intro', t('subscription.paywall.body'), true)}

        <div>
          <label className="mb-1 block text-[11px] font-medium text-gray-600">Reassurance items (under the plans, up to {MAX_REASSURANCE})</label>
          <div className="space-y-2">
            {content.reassurance.map((item, i) => {
              const Icon = REASSURANCE_ICONS[item.icon]
              return (
                <div key={i} className="flex flex-wrap items-start gap-2 rounded-lg border border-gray-200 p-2">
                  <div className="flex items-center gap-1">
                    <Icon className="h-4 w-4 text-gray-600" />
                    <select
                      value={item.icon}
                      onChange={(e) => setItem(i, { ...item, icon: e.target.value as ReassuranceItem['icon'] })}
                      className="rounded border border-gray-300 bg-white px-1.5 py-1 text-xs"
                    >
                      {PAYWALL_ICONS.map((icon) => (
                        <option key={icon} value={icon}>
                          {ICON_NAMES[icon]}
                        </option>
                      ))}
                    </select>
                  </div>
                  <LocalizedInput
                    className="w-40"
                    language={language}
                    path={`reassurance[${i}].title`}
                    value={item.title}
                    placeholder="No lock-in"
                    tokens={false}
                    onChange={(title) => setItem(i, { ...item, title })}
                  />
                  <LocalizedInput
                    className="min-w-48 flex-1"
                    language={language}
                    path={`reassurance[${i}].text`}
                    value={item.text}
                    placeholder="Cancel any time."
                    tokens={false}
                    onChange={(text) => setItem(i, { ...item, text })}
                  />
                  <button
                    type="button"
                    onClick={() => setContent((c) => ({ ...c, reassurance: c.reassurance.filter((_, j) => j !== i) }))}
                    className="mt-1 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-red-600"
                    title="Remove"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              )
            })}
            {content.reassurance.length < MAX_REASSURANCE && (
              <button
                type="button"
                onClick={() => setContent((c) => ({ ...c, reassurance: [...c.reassurance, { icon: 'shield', title: { en: '' }, text: { en: '' } }] }))}
                className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
              >
                <Plus className="h-3 w-3" /> Add item
              </button>
            )}
          </div>
        </div>

        {field('footnote', 'Footnote', t('subscription.paywall.noRollover'), true)}

        {error && <div className="rounded-lg border border-red-100 bg-red-50 p-2 text-sm text-red-700">{error.message}</div>}

        <div className="flex items-center gap-2">
          <Button size="sm" onClick={save} disabled={saving || !dirty}>
            {saving ? 'Saving…' : 'Save paywall texts'}
          </Button>
          {dirty && (
            <Button size="sm" variant="outline" disabled={saving} onClick={() => setContent(saved ?? EMPTY)}>
              Discard changes
            </Button>
          )}
        </div>

        {showPreview && (
          <div className="overflow-hidden rounded-xl border border-gray-200 bg-[#F7F9F6]">
            <div className="flex justify-center px-6 py-8" style={{ zoom: 0.7 }}>
              <PaywallPage plans={plans} content={cleaned} language={language} ctaFor={() => ({})} empty={<p className="text-center text-sm text-gray-500">No active plan to show.</p>} />
            </div>
          </div>
        )}
      </div>
    </ErrorFieldContext.Provider>
  )
}

export default PaywallContentPanel
